"use client";

import { useAppDispatch } from "@/redux/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useTransition,
} from "react";
import { useForm, useWatch } from "react-hook-form";

import { TextShimmer } from "@/components/text-shimmer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { setHotelExpiredTimer } from "@/feature/hotel/checkout/state/hotel-data.slice";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import { sanitizeSearchData } from "@/feature/hotel/search/lib/hotel-search-sanitizer";
import {
  createInitialHotelSearchData,
  getLastHotelSearch,
  saveLastHotelSearch,
} from "@/feature/hotel/search/params/hotel-search-persistence";
import { buildCustomAwareHotelSearchHref } from "@/feature/umrah/custom/request/custom-umrah-flow";
import { useSearchServiceFlow } from "@/feature/umrah/custom/request/hooks/use-search-service-flow";
import { serializeHotelSearchData } from "@/feature/umrah/custom/request/state/custom-umrah-flow.persistence";
import { setCustomUmrahHotelSearch } from "@/feature/umrah/custom/request/state/custom-umrah-flow.slice";

import { baseApi } from "@/redux/api/base-api";
import { hotelSearchSchema } from "../../schema/hotel-search-zod.schema";
import { requestHotelFilterResetOnNextSearch } from "../../state/extract-hotel-filter-slice";
import { IHotelsSearchSchema } from "../../types/hotel-search-type";
import { HotelFields } from "./hotel-fields";

/* ---------- component ---------- */

interface IProps {
  setOpen?: Dispatch<SetStateAction<boolean>>;
  open?: boolean;
  hiddenWidget?: boolean;
  searchData?: IHotelsSearchSchema;
}

export default function HotelSearchForm({
  open: _open,
  searchData,
  setOpen,
  hiddenWidget: _hiddenWidget,
}: IProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [isPending, startTransition] = useTransition();
  const {
    isCustomUmrahFlow,
    source,
    umrahCity: customUmrahCity,
  } = useSearchServiceFlow();

  const defaultValues = useMemo(() => {
    const initial = createInitialHotelSearchData(searchData);
    return sanitizeSearchData(initial);
  }, [searchData]);

  const form = useForm<IHotelsSearchSchema>({
    resolver: zodResolver(hotelSearchSchema),
    defaultValues,
  });

  useEffect(() => {
    if (searchData) {
      return;
    }

    const storedSearch = getLastHotelSearch();
    if (!storedSearch) {
      return;
    }

    form.reset(sanitizeSearchData(storedSearch));
  }, [form, searchData]);

  const rooms = useWatch({
    control: form.control,
    name: "rooms",
    defaultValue: defaultValues.rooms,
  });

  const totalGuests = useMemo(() => {
    return (rooms ?? []).reduce((total, room) => {
      return total + room.adults + room.children + room.infants;
    }, 0);
  }, [rooms]);

  const onSubmit = useCallback(
    (data: IHotelsSearchSchema) => {
      dispatch(setHotelExpiredTimer(0));
      setOpen?.(false);
      saveLastHotelSearch(data);
      dispatch(requestHotelFilterResetOnNextSearch());
      dispatch(baseApi.util.invalidateTags(["HOTEL_SEARCH"]));

      if (isCustomUmrahFlow && customUmrahCity) {
        dispatch(
          setCustomUmrahHotelSearch({
            city: customUmrahCity,
            search: serializeHotelSearchData(data),
          }),
        );
      }
      const params = encodedHotelParams(data);

      startTransition(() => {
        router.push(
          isCustomUmrahFlow
            ? buildCustomAwareHotelSearchHref(data, {
                cityKey: customUmrahCity,
                source,
              })
            : `/hotels?${params}`,
        );
      });
    },
    [customUmrahCity, dispatch, isCustomUmrahFlow, router, setOpen, source],
  );

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
      <div className="mb-5 flex flex-wrap items-center gap-2 md:gap-4">
        <HotelFields.SearchType />
        <HotelFields.Guests totalGuests={totalGuests} />
        <HotelFields.Rooms totalRooms={(rooms ?? []).length} />
      </div>

      <div
        className={cn(
          "grid items-start gap-3",
          "grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_repeat(2,minmax(0,1fr))_52px] xl:grid-cols-[minmax(0,1.2fr)_repeat(2,minmax(0,1fr))_56px]",
        )}
      >
        <HotelFields.Location form={form} disabled={isCustomUmrahFlow} />
        <HotelFields.DateRange form={form} />
        <HotelFields.RoomAndGuests form={form} />

        <Button
          type="submit"
          className="h-12 rounded-md bg-primary px-4 text-primary-foreground hover:bg-primary/90 md:size-13 md:min-w-13 md:px-0 xl:size-14 xl:min-w-14"
          size="lg"
          disabled={isPending}
          aria-label="Search hotels"
        >
          <Search className="h-5 w-5" />
          <span className="md:hidden">
            {isPending ? (
              <TextShimmer duration={1.2}>Search</TextShimmer>
            ) : (
              "Search"
            )}
          </span>
        </Button>
      </div>
    </form>
  );
}
